import axios from "axios";
import { toast } from "sonner";

export const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

// Filtro de departamento (Español / Inglés) que elige la administración arriba.
// Se manda como query param en todos los GET; los endpoints que no lo usan lo ignoran.
export function setDepartmentFilter(depId) {
  if (depId) localStorage.setItem("dept_filter", depId);
  else localStorage.removeItem("dept_filter");
}
export function getDepartmentFilter() {
  return localStorage.getItem("dept_filter") || "";
}

// Algunas pantallas se abren fijadas a un departamento con ?dept=... en la URL
// (ej. "Finanzas WSS" y "Finanzas WSE" en la vista consolidada). Eso manda sobre
// el selector de arriba solo para las llamadas de esa pantalla.
export function getEffectiveDepartment() {
  try {
    const fijo = new URLSearchParams(window.location.search).get("dept");
    if (fijo !== null) return fijo;
  } catch {
    /* ignore */
  }
  return getDepartmentFilter();
}
api.interceptors.request.use((config) => {
  const dep = getEffectiveDepartment();
  if (dep && (config.method || "get").toLowerCase() === "get") {
    config.params = { department_id: dep, ...(config.params || {}) };
  }
  return config;
});

// Allow Bearer fallback if cookies don't flow cross-origin in some browsers
let bearer = null;
export function setBearer(token) {
  bearer = token;
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
}

api.interceptors.request.use((config) => {
  if (bearer && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${bearer}`;
  }
  return config;
});

// "Ver como" (impersonación): el backend responde 403 en cualquier escritura.
// Además: las lecturas (GET) que fallan por red / 5xx / timeout se reintentan solas
// (2 veces, con espera creciente) antes de fallar, para que un corte momentáneo
// no deje las tablas en "Sin datos". Si igual falla, se avisa una vez.
const RETRY_STATUSES = new Set([0, 408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
const MAX_RETRIES = 2;
let lastNetToast = 0;

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

api.defaults.timeout = 25000;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config || {};
    const status = error?.response?.status ?? 0;
    const isGet = (cfg.method || "get").toLowerCase() === "get";
    const retriable = isGet && (RETRY_STATUSES.has(status) || error?.code === "ECONNABORTED" || error?.code === "ERR_NETWORK");
    if (retriable && (cfg.__retryCount || 0) < MAX_RETRIES) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      await sleep(600 * cfg.__retryCount);
      return api.request(cfg);
    }
    if (retriable && Date.now() - lastNetToast > 8000) {
      lastNetToast = Date.now();
      toast.error("No se pudo cargar la información. Revisá tu conexión o volvé a intentar.", {
        action: { label: "Reintentar", onClick: () => window.location.reload() },
      });
    }
    const detail = error?.response?.data?.detail;
    // Pase de WSS Live sin pagar: en vez de llenar la pantalla de errores, lo
    // mandamos a la única pantalla que tiene abierta, la de sus pagos.
    if (status === 402) {
      if (!window.location.pathname.endsWith("/my-payments")) {
        window.location.assign("/dashboard/my-payments");
      }
      return Promise.reject(error);
    }
    if (error?.response?.status === 403 && typeof detail === "string" && detail.includes("solo lectura")) {
      toast.info("Modo solo lectura: estás viendo como otra persona.");
    }
    return Promise.reject(error);
  }
);
